import * as React from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/core/components/ui/tooltip";
import { Typography } from "@/core/components/ui/typography";
import { cn } from "@/core/utils/utils";

// Add type to distinguish between icon and text adornments
type Adornment = React.ReactNode | string;

// placeholder will default to label if not provided
export interface InputProps extends React.ComponentProps<"input"> {
  label?: string;
  error?: string;
  fullWidth?: boolean;
  RightActionComponent?: React.ReactNode;
  disableRequirementAsterisk?: boolean;
  persistentLabel?: boolean;
  startAdornment?: Adornment;
  endAdornment?: Adornment;
  disabledTooltip?: React.ReactNode | string;
}

// Helper function to determine if adornment is a string
const isTextAdornment = (adornment: Adornment): adornment is string =>
  typeof adornment === "string";

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      label,
      fullWidth,
      error,
      RightActionComponent,
      required,
      disableRequirementAsterisk,
      value,
      defaultValue,
      onChange,
      persistentLabel,
      placeholder,
      startAdornment,
      endAdornment,
      disabled,
      disabledTooltip,
      ...props
    },
    ref,
  ) => {
    // Track if input has content
    const [hasContent, setHasContent] = React.useState(
      Boolean(value || defaultValue),
    );

    React.useEffect(() => {
      setHasContent(Boolean(value));
    }, [value]);

    // Handle content changes
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setHasContent(Boolean(e.target.value));
      onChange?.(e);
    };

    const plainPlaceholder = !placeholder ? label : placeholder;
    const placeholderWithAsterisk =
      required && !disableRequirementAsterisk && !persistentLabel
        ? `${plainPlaceholder} *`
        : plainPlaceholder;

    const renderAdornment = (
      adornment: Adornment,
      position: "start" | "end",
    ) => {
      if (!adornment) return null;

      const isText = isTextAdornment(adornment);
      const baseClassName = cn(
        "absolute z-20 text-secondary-text text-lg top-[18px]",
        isText && "pointer-events-none",
        disabled && "text-tertiary-text",
        position === "start" ? "left-4" : "right-4",
        !isText && "top-[16px]",
        !isText && "peer-focus-visible:text-interactive-text",
        isText && position === "start" && hasContent && "top-[1.6rem]",
        error && "text-destructive",
      );

      return (
        <div className={baseClassName}>
          {adornment}
          {isText && position === "start" && (
            <span className="ml-1 text-xl font-bold text-tertiary-text">|</span>
          )}
        </div>
      );
    };

    const inputElement = (
      <div className={cn("relative min-h-[56px] p-px", fullWidth && "w-full")}>
        {label && (persistentLabel || hasContent) && (
          <Typography
            variant="subtitleW3"
            className={cn(
              "absolute left-4 top-2 z-20 text-tertiary-text",
              startAdornment && !isTextAdornment(startAdornment) && "left-12",
              error && "text-destructive",
            )}
          >
            {label}
            {required && !disableRequirementAsterisk && (
              <span className="ml-1">*</span>
            )}
          </Typography>
        )}
        <div className="relative" style={{ transform: "translate3d(0, 0, 0)" }}>
          <input
            className={cn(
              "peer flex h-14 w-full rounded-lg border border-input bg-card px-4 text-lg shadow-sm transition-colors",
              "file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground",
              "placeholder:text-lg placeholder:text-tertiary-text",
              "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
              "disabled:cursor-not-allowed disabled:border-stroke-tertiary disabled:bg-bg-secondary disabled:text-tertiary-text",
              "autofill:shadow-[inset_0_0_0px_1000px_#D4F6EE]",
              "relative",
              error && "border-destructive focus-visible:ring-destructive/50",
              (persistentLabel || (hasContent && label)) && "pb-2 pt-6",
              startAdornment &&
                (isTextAdornment(startAdornment) ? "pl-11" : "pl-12"),
              endAdornment &&
                (isTextAdornment(endAdornment) ? "pr-8" : "pr-12"),
              className,
            )}
            ref={ref}
            value={value}
            defaultValue={defaultValue}
            onChange={handleChange}
            placeholder={placeholderWithAsterisk}
            required={required}
            disabled={disabled}
            {...props}
          />
          {renderAdornment(startAdornment, "start")}
          {renderAdornment(endAdornment, "end")}
        </div>
        {RightActionComponent && (
          <div className="absolute right-4 top-0">{RightActionComponent}</div>
        )}
      </div>
    );

    if (disabled && disabledTooltip) {
      return (
        <Tooltip>
          <TooltipTrigger asDiv className={cn(fullWidth && "w-full")}>
            {inputElement}
          </TooltipTrigger>
          <TooltipContent>{disabledTooltip}</TooltipContent>
        </Tooltip>
      );
    }

    return inputElement;
  },
);

Input.displayName = "Input";

export { Input };
